import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react';
import { cn } from 'utils/cn';
import { TAB_VARIANT_CLASSES, useTabsContext } from './Tabs';

export interface TabProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  index?: number;
}

function Tab({
  children,
  className,
  index,
  disabled,
  onClick,
  ...props
}: TabProps) {
  const { selectedIndex, onSelect, variant, selectedTabClassName, baseId } =
    useTabsContext();
  const isSelected = index === selectedIndex;
  const variantClasses = TAB_VARIANT_CLASSES[variant];

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    onClick?.(event);
    if (event.defaultPrevented || disabled || index === undefined) {
      return;
    }
    onSelect(index);
  };

  return (
    <button
      type='button'
      role='tab'
      id={`${baseId}-tab-${index}`}
      aria-controls={`${baseId}-panel-${index}`}
      aria-selected={isSelected}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        variantClasses.tab,
        isSelected ? variantClasses.tabActive : variantClasses.tabInactive,
        isSelected && selectedTabClassName,
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      {...props}>
      {children}
    </button>
  );
}

Tab.displayName = 'UIKitTab';

export { Tab };
